import React, { useCallback, useEffect } from 'react';
import Checkmark from '@uber/base-icons/checkmark';
import Hide from '@uber/base-icons/hide';
import Pencil from '@uber/base-icons/pencil';
import Show from '@uber/base-icons/show';
import SpeechBubble from '@uber/base-icons/speech-bubble';
import X from '@uber/base-icons/x';
import { useStyletron } from 'baseui';
import { Button, KIND as BUTTON_KIND, SHAPE as BUTTON_SHAPE, SIZE as BUTTON_SIZE } from 'baseui/button';
import useShortcut from '../../../image/labelling/providers/hooks/use-shortcut';
import { handleShortcutRegistrationError } from '../../../image/labelling/providers/utils/shortcut';
import { useReviewMode } from '../../providers/review-mode-providers';
import { usePubSub } from '../../pub-sub';
import { useEditAnnotationHandlers, useReviewFeature } from '../hooks';
import { useReviewModal } from '../review-modal/use-review-modal';
import { isClassificationAnnotationId } from '../utils';
import { EVENTS } from './constants';
import { useGetBatchModePermissions } from './useGetBatchModePermissions';
type BatchItemActionProps = {
  expanded?: boolean;
  currentId: string;
  hidden?: boolean;
};
const buttonProps = {
  size: BUTTON_SIZE.compact,
  shape: BUTTON_SHAPE.circle,
  kind: BUTTON_KIND.tertiary
};
export const BatchItemAction = ({
  expanded,
  currentId,
  hidden
}: BatchItemActionProps) => {
  const [css, theme] = useStyletron();
  const {
    addComment
  } = useReviewModal();
  const {
    canEdit
  } = useReviewFeature();
  const {
    onEditCallback
  } = useEditAnnotationHandlers(canEdit);
  const {
    publish
  } = usePubSub();
  const permissions = useGetBatchModePermissions();
  const {
    registerHotKeys
  } = useShortcut();
  const {
    toggleAnnotationVisibility
  } = useReviewMode();
  const isClassificationAnnotation = isClassificationAnnotationId(currentId);
  const iconColor = isClassificationAnnotation ? theme.colors.contentStateDisabled : theme.colors.contentPrimary;
  const handleEditCallback = useCallback(() => {
    onEditCallback?.(currentId);
  }, [onEditCallback, currentId]);
  useEffect(() => {
    if (!expanded || !permissions.isEditable || !onEditCallback) {
      return;
    }
    const {
      removeHotKeys,
      errors
    } = registerHotKeys([{
      keys: ['e'],
      onAction: handleEditCallback
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [expanded, permissions.isEditable, onEditCallback, registerHotKeys, handleEditCallback]);
  return <section className={css({
    display: 'flex',
    gap: theme.sizing.scale200,
    flexBasis: '35%',
    justifyContent: 'flex-end'
  })}>
      {toggleAnnotationVisibility && <Button {...buttonProps} data-testid="show-hide-button" disabled={isClassificationAnnotation} onClick={e => {
      e.stopPropagation();
      toggleAnnotationVisibility(!hidden, currentId);
    }}>
          {hidden ? <Hide color={iconColor} size={theme.sizing.scale600} /> : <Show color={iconColor} size={theme.sizing.scale600} />}
        </Button>}
      {permissions.canComment && <Button {...buttonProps} data-testid="comment-button" onClick={e => {
      e.stopPropagation();
      addComment(currentId);
      publish(EVENTS.COMMENT_MODAL_OPEN);
    }}>
          <SpeechBubble color={theme.colors.contentPrimary} size={theme.sizing.scale600} />
        </Button>}
      {permissions.isEditable && onEditCallback && <Button {...buttonProps} onClick={handleEditCallback}>
          <Pencil color={theme.colors.contentPrimary} size={theme.sizing.scale600} />
        </Button>}
      {permissions.canTakeActions && <>
          <Button {...buttonProps} kind={expanded ? BUTTON_KIND.primary : BUTTON_KIND.secondary}>
            <X color={expanded ? theme.colors.contentInversePrimary : theme.colors.contentPrimary} size={theme.sizing.scale600} />
          </Button>
          <Button {...buttonProps} kind={expanded ? BUTTON_KIND.primary : BUTTON_KIND.secondary}>
            <Checkmark color={expanded ? theme.colors.contentInversePrimary : theme.colors.contentPrimary} size={theme.sizing.scale600} />
          </Button>
        </>}
    </section>;
};